import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileWithPath, useDropzone, FileRejection } from "react-dropzone";
import { FileDetails } from "@/types/types";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { PlusIcon, X, Video, AlertCircle, Download } from "lucide-react";
import VideoPlayerV3, { DEFAULT_BLURHASH } from "./VideoPlayerV3";
import { getHashAndThumbnailsForVideos } from "@/utils/getHashandthumbnail";
import UploadFileImg from "@/assets/images/auth/upload-file.webp";
import toast from "react-hot-toast";

interface FileUploadProps {
  videoId?: number;
  onFilesUploaded?: (files: FileDetails[]) => void;
  maxFiles?: number;
  defaultValue?: FileDetails[];
  maxSize?: number;
  accept?: { [key: string]: string[] };
  clearFiles?: boolean;
  showSelectedFiles?: boolean;
  useExternalState?: boolean;
  externalFiles?: FileDetails[];
  useLiveUrl?: boolean;
  cardDesignSize?: string;
  height: string;
  showUploadAnimationOnly?: boolean;
  width: string;
  setExternalFiles?: React.Dispatch<React.SetStateAction<FileDetails[]>>;
  setVideosAreUpload?: React.Dispatch<React.SetStateAction<string | null>>;
}

const VideoUploaderWithPreview: React.FC<FileUploadProps> = ({
  onFilesUploaded,
  maxFiles = 10,
  showUploadAnimationOnly = false,
  useLiveUrl = false,
  cardDesignSize = "size-[124px]",
  height,
  width,
  showSelectedFiles = true,
  defaultValue = [],
  useExternalState = false,
  externalFiles = [],
  setExternalFiles,
  setVideosAreUpload,
  maxSize = 200 * 1024 * 1024, // 200MB default
  accept = {
    "video/mp4": [".mp4"],
    "video/quicktime": [".mov"],
    "image/gif": [],
  },
  clearFiles = false,
}) => {
  const [playingVideoUrl, setPlayingVideoUrl] = useState("");
  const [localFiles, setLocalFiles] = useState<FileDetails[]>(defaultValue);
  const [uploading, setUploading] = useState<boolean>(false);
  const previewUrls = useRef(new Set<string>());
  const [uploadSteps, setUploadSteps] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  );

  const files = useMemo(
    () => (useExternalState ? externalFiles : localFiles),
    [useExternalState, externalFiles, localFiles],
  );

  const setFiles = useMemo(
    () => (useExternalState ? setExternalFiles : setLocalFiles),
    [useExternalState, setExternalFiles],
  );

  const cardDimensions = useMemo(
    () => ({
      width: width || "124px",
      height: height || "124px",
    }),
    [width, height],
  );

  const maxSizeLabel = useMemo(() => {
    return `${Math.round(maxSize / (1024 * 1024))} MB`;
  }, [maxSize]);

  useEffect(() => {
    if (clearFiles && setFiles) {
      setFiles([]);
      setUploadSteps({});
      setErrors({});
      setUploadProgress({});
    }
  }, [clearFiles, setFiles]);

  useEffect(() => {
    return () => {
      previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrls.current.clear();
    };
  }, []);

  const sanitizeFileName = useCallback((name: string): string => {
    return name.replace(/[^a-zA-Z0-9.\-_]/g, "_").slice(0, 100);
  }, []);

  const generateUniqueFileName = useCallback(
    (originalName: string): string => {
      const timeStamp = crypto.randomUUID();
      const sanitized = sanitizeFileName(originalName);
      const lastDot = sanitized.lastIndexOf(".");

      if (lastDot > 0 && lastDot < sanitized.length - 1) {
        const base = sanitized.slice(0, lastDot);
        const ext = sanitized.slice(lastDot);
        return `${base}-${timeStamp}${ext}`;
      }

      return `${sanitized}-${timeStamp}`;
    },
    [sanitizeFileName],
  );

  const getDisplayFileName = useCallback((storedName: string): string => {
    // Current: "video-1716192000000.mp4" → "video.mp4"
    const withoutTsBeforeExt = storedName.replace(
      /-\d{12,17}(\.[^./\\]+)$/i,
      "$1",
    );
    if (withoutTsBeforeExt !== storedName) return withoutTsBeforeExt;

    // Legacy: "video.mp4-1716192000000" → "video.mp4"
    return storedName.replace(/-\d{12,17}$/, "");
  }, []);

  const updateUploadStep = useCallback((filename: string, step: string) => {
    setUploadSteps((prev) => ({ ...prev, [filename]: step }));
  }, []);

  const clearUploadedStep = useCallback((filename: string) => {
    setUploadSteps((prev) => {
      const updated = { ...prev };
      delete updated[filename];
      return updated;
    });
  }, []);

  const setFileError = useCallback((fileName: string, error: string) => {
    setErrors((prev) => ({ ...prev, [fileName]: error }));
  }, []);

  const clearFileError = useCallback((fileName: string) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[fileName];
      return updated;
    });
  }, []);

  const updateProgress = useCallback((fileName: string, progress: number) => {
    setUploadProgress((prev) => ({
      ...prev,
      [fileName]: Math.max(0, Math.min(100, progress)),
    }));
  }, []);

  const clearProgress = useCallback((fileName: string) => {
    setUploadProgress((prev) => {
      const updated = { ...prev };
      delete updated[fileName];
      return updated;
    });
  }, []);

  const processFileUpload = useCallback(
    async (fileDetails: FileDetails): Promise<FileDetails> => {
      try {
        updateUploadStep(fileDetails.name, "Uploading video...");
        updateProgress(fileDetails.name, 0);
        clearFileError(fileDetails.name);

        const uploadedFile = await UploadFileToS3(fileDetails, undefined, {
          onProgress: (percent) =>
            updateProgress(fileDetails.name, Math.round(percent * 0.95)),
        });
        updateProgress(fileDetails.name, 95);
        updateUploadStep(fileDetails.name, "Generating preview...");

        const hashAndThumbnail = await getHashAndThumbnailsForVideos({
          videoUrls: [uploadedFile.live_url as string],
        });

        updateProgress(fileDetails.name, 100);

        const completedFile: FileDetails = {
          ...uploadedFile,
          // Keep the UI identity while saving the actual uploaded metadata.
          name: fileDetails.name,
          local_url: uploadedFile.live_url as string,
          hash: hashAndThumbnail?.[0]?.hash,
          thumbnail: hashAndThumbnail?.[0]?.thumbnail,
        };

        setTimeout(() => {
          clearUploadedStep(fileDetails.name);
          clearProgress(fileDetails.name);
        }, 300);

        return completedFile;
      } catch (error) {
        clearUploadedStep(fileDetails.name);
        clearProgress(fileDetails.name);

        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        setFileError(fileDetails.name, errorMessage);
        toast.error(errorMessage);
        console.error(`Failed to upload file: ${fileDetails.name}`, error);

        throw error;
      } finally {
        URL.revokeObjectURL(fileDetails.local_url);
        previewUrls.current.delete(fileDetails.local_url);
      }
    },
    [
      setVideosAreUpload,
      updateUploadStep,
      updateProgress,
      clearUploadedStep,
      clearProgress,
      clearFileError,
      setFileError,
    ],
  );

  const onDrop = useCallback(
    async (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
      if (!setFiles) return;

      if (fileRejections.length > 0) {
        fileRejections.forEach((rejection) => {
          const fileName = rejection.file.name;
          const tooLarge = rejection.errors.some(
            (e) => e.code === "file-too-large",
          );
          const invalidType = rejection.errors.some(
            (e) => e.code === "file-invalid-type",
          );

          if (tooLarge) {
            toast.error(
              `${fileName} is larger than ${maxSizeLabel}. Please upload a smaller video.`,
            );
          } else if (invalidType) {
            toast.error(`${fileName} has an unsupported format.`);
          } else {
            toast.error(rejection.errors[0]?.message || "File upload failed.");
          }
        });
      }

      if (uploading) return;

      const availableSlots = maxFiles - files.length;
      if (availableSlots <= 0) {
        toast.error(`You can upload up to ${maxFiles} files only.`);
        return;
      }

      const filesToProcess = acceptedFiles.slice(0, availableSlots);
      if (!filesToProcess.length) return;

      setUploading(true);

      const pendingFiles = filesToProcess.map((file) => {
        const pendingName = generateUniqueFileName(file.name);
        const localUrl = URL.createObjectURL(file);
        previewUrls.current.add(localUrl);
        return {
          name: pendingName,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          webkitRelativePath: file.webkitRelativePath,
          local_url: localUrl,
          file,
          live_url: null,
          thumbnail: null,
          hash: null,
          uploading: true,
        };
      });

      setFiles((prevFiles) => [...prevFiles, ...pendingFiles]);
      setVideosAreUpload?.(pendingFiles[0].name);

      const uploadPromises = filesToProcess.map(async (file, index) => {
        try {
          const completedFile = await processFileUpload(pendingFiles[index]);

          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.name === pendingFiles[index].name ? completedFile : f,
            ),
          );

          return completedFile;
        } catch {
          setFiles((prevFiles) =>
            prevFiles.filter((f) => f.name !== pendingFiles[index].name),
          );
          return null;
        }
      });

      try {
        const results = await Promise.allSettled(uploadPromises);

        const successfulUploads = results
          .filter(
            (result): result is PromiseFulfilledResult<FileDetails | null> =>
              result.status === "fulfilled" && result.value !== null,
          )
          .map((result) => result.value as FileDetails);

        onFilesUploaded?.(successfulUploads);
      } finally {
        setUploading(false);
        setVideosAreUpload?.(null);
      }
    },
    [
      setFiles,
      uploading,
      maxFiles,
      files.length,
      generateUniqueFileName,
      processFileUpload,
      onFilesUploaded,
      maxSizeLabel,
      setVideosAreUpload,
    ],
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      if (!setFiles) return;

      const fileToRemove = files[index];

      if (fileToRemove.local_url) {
        URL.revokeObjectURL(fileToRemove.local_url);
      }

      clearUploadedStep(fileToRemove.name);
      clearFileError(fileToRemove.name);
      clearProgress(fileToRemove.name);

      const updatedFiles = files.filter((_, i) => i !== index);
      setFiles(updatedFiles);
      onFilesUploaded?.(updatedFiles);
    },
    [
      setFiles,
      files,
      clearUploadedStep,
      clearFileError,
      clearProgress,
      onFilesUploaded,
    ],
  );

  const handlePlayVideo = useCallback((url: string) => {
    setPlayingVideoUrl((current) => (current === url ? "" : url));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxSize,
    accept,
    disabled: uploading,
  });

  const UploadingCard = ({
    fileName,
    step,
    progress = 0,
  }: {
    fileName?: string;
    step?: string;
    progress?: number;
  }) => (
    <div
      style={cardDimensions}
      className="relative overflow-hidden rounded-xl border border-[#E6E8EC] bg-white p-3 shadow-sm"
    >
      <div className="flex h-full flex-col justify-between">
        <div className="flex items-start justify-between">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FFF4EC]">
            <Video className="h-4 w-4 text-[#FF6600]" />
          </div>
          <span className="text-[10px] font-medium text-[#98A2B3]">
            {progress}%
          </span>
        </div>

        <div className="mt-3">
          <p className="line-clamp-1 text-xs font-medium text-[#101828]">
            {fileName || "Uploading video"}
          </p>
          <p className="mt-1 text-[11px] text-[#667085]">
            {step || "Uploading..."}
          </p>

          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#F2F4F7]">
            <div
              className="h-full rounded-full bg-[#FF6600] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const FileCard = ({ file, index }: { file: FileDetails; index: number }) => {
    const isUploading = uploadSteps[file.name];
    const hasError = errors[file.name];
    const progress = uploadProgress[file.name] ?? 0;

    if (isUploading) {
      return (
        <UploadingCard
          fileName={getDisplayFileName(file.name)}
          step={uploadSteps[file.name]}
          progress={progress}
        />
      );
    }

    return (
      <div
        className="group relative flex items-center justify-center overflow-hidden rounded-xl border border-[#EAECF0] bg-[#F8F8F8]"
        style={cardDimensions}
      >
        {hasError ? (
          <div className="flex h-full w-full flex-col items-center justify-center px-3 text-center">
            <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-xs font-medium text-red-500">Upload failed</p>
            <p className="mt-1 line-clamp-2 text-[11px] text-[#667085]">
              {hasError}
            </p>
            <button
              onClick={() => handleRemoveFile(index)}
              className="mt-2 text-xs font-medium text-[#344054] hover:text-[#101828]"
            >
              Remove
            </button>
          </div>
        ) : (
          <>
            <VideoPlayerV3
              url={file.live_url as string}
              thumbnailUrl={file.thumbnail as string}
              playing={playingVideoUrl === file.live_url}
              muted={false}
              blurhash={file?.hash ? file.hash : (DEFAULT_BLURHASH as string)}
              onPlay={() => handlePlayVideo(file.live_url as string)}
              border="rounded-xl"
            />
            <button
              className="absolute right-2 top-2 z-[999] rounded-full border border-gray-200 bg-white p-1.5 shadow-sm transition hover:bg-gray-100"
              onClick={() => handleRemoveFile(index)}
              aria-label="Remove file"
            >
              <X size={12} />
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex w-full flex-wrap items-start gap-x-4 gap-y-3">
      {(files.length < maxFiles || !showSelectedFiles) && (
        <div className="flex w-full flex-col gap-2 md:w-auto">
          <div
            {...getRootProps()}
            className={`
            relative overflow-hidden transition-all duration-200
            ${cardDesignSize}
            ${
              isDragActive
                ? "border-[#FF6600] bg-[#FFF5EF]"
                : "border-[#D0D5DD] bg-[#FCFCFD] hover:border-[#FF6600] hover:bg-[#FFF9F6] cursor-pointer"
            }
            ${uploading ? "pointer-events-none opacity-70" : ""}
          `}
            style={cardDimensions}
          >
            <input
              {...getInputProps()}
              className="absolute left-0 top-0 h-full w-full opacity-0"
            />

            <div className="pointer-events-none flex h-full w-full flex-col items-center justify-between px-1.5 py-2 relative">
              <div className="flex w-full justify-center">
                <img
                  src={UploadFileImg}
                  alt="Upload preview"
                  className="h-auto w-[124px] object-contain"
                />
              </div>

              <div className="absolute bottom-0 flex items-center justify-center gap-1 rounded-[8px] border border-[#D0D5DD] bg-white py-1.5 px-1 z-[50] md:w-full">
                <span>
                  <Download color="#FF6600" size={10} />
                </span>
                <span className="text-[10px] font-medium leading-none text-[#344054]">
                  Upload
                </span>
                <span className="text-[8px] font-normal text-[#667085]">
                  (max {maxSizeLabel})
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSelectedFiles && !showUploadAnimationOnly && (
        <>
          {files.map((file, index) => (
            <FileCard key={`${file.name}-${index}`} file={file} index={index} />
          ))}
        </>
      )}

      {uploading && showUploadAnimationOnly && <UploadingCard progress={35} />}
    </div>
  );
};

export default VideoUploaderWithPreview;
